import clsx from "clsx";
import { useMemo } from "react";
import { api } from "../../api";
import LoadingText from "../LoadingText";
import { Badge } from "../tailwind/badge";
import { Divider } from "../tailwind/divider";
import { Text } from "../tailwind/text";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { useFindMany } from "@gadgetinc/react";
import { calculateRate } from "../../functions/calculateRate";
import { PencilIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/16/solid";
import { formatDateTime } from "../../functions/date-utils";
import { Button } from "../tailwind/button";
import { useNavigate } from "react-router";
import { Link } from "../tailwind/link";

export function ViewPastCampaigns({
  customerId,
  className,
  isMobile,
  integrations,
}) {
  const navigate = useNavigate();
  const [{ data: lastCampaigns, fetching }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customerId },
      isArchived: { notEquals: true },
      trigger: { equals: "manual" },
      sendings: {
        some: {
          status: { equals: "completed" },
        },
      },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      scheduledDate: true,
      messagesCount: true,
      sent: true,
      read: true,
      delivered: true,
      attributedRevenueShopify: true,
    },
    first: 10,
  });

  const lastCampaignsSent = useMemo(() => {
    return (lastCampaigns ?? [])
      .filter((campaign) => campaign.scheduledDate)
      .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
      .slice(0, 3);
  }, [lastCampaigns]);

  return (
    <div className={clsx(className, "border rounded-xl p-3 shadow bg-white")}>
      <Link onClick={() => navigate("/campaigns")}>
        <div className="flex items-center hover:underline hover:cursor-pointer">
          <Text className="text-lg font-bold mb-2">Latest campaigns</Text>
          <ChevronRightIcon className="h-6 w-6 mb-1" />
        </div>
      </Link>
      <Divider />

      <Table dense>
        <TableHead>
          <TableRow>
            {!isMobile && <TableHeader>Status</TableHeader>}
            <TableHeader>Campaign</TableHeader>
            <TableHeader>Last sending</TableHeader>
            <TableHeader className="text-center">Audience</TableHeader>
            {!isMobile && (
              <>
                <TableHeader className="text-center">Delivered</TableHeader>
                <TableHeader className="text-center">Open rate</TableHeader>
                {integrations.shopify && (
                  <TableHeader className="text-center">Turnover</TableHeader>
                )}
                <TableHeader className="text-center"></TableHeader>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {fetching ? (
            <TableRow>
              <TableCell colSpan={7}>
                <LoadingText className="my-3 h-[24px]" />
              </TableCell>
            </TableRow>
          ) : lastCampaignsSent.length > 0 ? (
            lastCampaignsSent.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                integrations={integrations}
                isMobile={isMobile}
                navigate={navigate}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <Text className="text-center py-4 text-lg text-gray-700">
                  Start sending campaigns 📢
                </Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CampaignRow({ campaign, integrations, isMobile, navigate }) {
  const openRate =
    campaign.read > 0 && campaign.sent > 0
      ? calculateRate(campaign.read, campaign.delivered).toFixed(0)
      : null;

  return (
    <TableRow
      key={campaign.id}
      href={`/analytics/${campaign.id}`}
      className="group hover:bg-muted transition"
    >
      {!isMobile && (
        <TableCell>
          <Badge color="lime">Sent</Badge>
        </TableCell>
      )}
      <TableCell>{campaign.name}</TableCell>
      <TableCell>
        {campaign.scheduledDate
          ? isMobile
            ? formatDateTime(new Date(campaign.scheduledDate), "shortDate")
            : formatDateTime(
                new Date(campaign.scheduledDate),
                "shortTimeMediumDate",
              )
          : "-"}
      </TableCell>
      <TableCell className="text-center">
        {campaign.messagesCount ?? "-"}
      </TableCell>
      {!isMobile && (
        <>
          <TableCell className="text-center">
            {campaign.delivered ?? "-"}
          </TableCell>
          <TableCell className="text-center">
            {openRate ? `${openRate}%` : "-"}
          </TableCell>
          {integrations.shopify && (
            <TableCell className="text-center">
              {campaign.attributedRevenueShopify
                ? `${campaign.attributedRevenueShopify.toFixed(0)}€`
                : "-"}
            </TableCell>
          )}
          <TableCell className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Button
              plain
              className="h-[25px]"
              aria-label="Edit campaign"
              onClick={() => {
                navigate(`/campaigns/edit/${campaign.id}`);
              }}
            >
              <PencilIcon className="w-4" />
            </Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
